import { Fragment } from "react";

import type { CalculationLineOut, LineKind } from "@/api";
import { formatArea, formatMoney } from "@/lib/format";

import styles from "./CalculationScreen.module.css";
import { PriceSourceNote } from "./PriceSourceNote";

/** The bracket group's one sentence (D-9): a sales price from JTL, no purchase price behind it. */
const JTL_SENTENCE = "Verkaufspreis lt. JTL, kein EK hinterlegt";

/** The five groups of design §9, in this order; an empty group is not drawn. */
const GROUPS: { kind: LineKind; title: string; note: string | null }[] = [
  { kind: "grating", title: "Roste", note: null },
  { kind: "console", title: "Winkelkonsolen", note: JTL_SENTENCE },
  { kind: "cutout", title: "Ausschnitte", note: null },
  { kind: "surcharge", title: "Zuschläge", note: null },
  { kind: "freight", title: "Fracht", note: null },
];

/**
 * Every position with its amounts and, under it, where the price came from.
 *
 * Nothing here adds: `ekTotal` and `vkTotal` are the server's per-line
 * figures, the sums live in `TotalsBlock` and are the server's too (D-17).
 * Nothing here words the source either: `sourceText` is the server's
 * sentence (`pricing/provenance.py`), the same one the internal workbook
 * prints — every line that has one shows it, brackets included (S2-6 F-1);
 * a line without a price has none, and its gap names the reason.
 *
 * `blockedReason`/`vkTotal` is a tri-state, not a boolean (fix round 1,
 * N-16): `null`/set is an ordinary priced line; set/`null` is a line with no
 * price at all — the four amount cells collapse into one "Preis fehlt —
 * …" cell, in red, with the word; set/set is a line the engine still priced
 * but flagged with a soft gap (`cut_rfq`/`cut_excluded`/
 * `rh_surcharge_missing`) — its amounts show exactly as an ordinary line's,
 * with a second line under the label naming the gap. Only the middle case
 * counts as "blocked": `data-blocked` and the conflict background follow
 * `blockedMessage`, not `line.blockedReason` alone.
 */
export function LineTable({ lines }: { lines: CalculationLineOut[] }) {
  const groups = GROUPS.map((group) => ({
    ...group,
    lines: lines.filter((line) => line.kind === group.kind),
  })).filter((group) => group.lines.length > 0);

  if (groups.length === 0) {
    return <p className={styles.note}>Keine Positionen — die Stückliste ist leer.</p>;
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <caption className="visually-hidden">Positionen der Kalkulation, gruppiert nach Art</caption>
        <thead>
          <tr>
            <th scope="col" className="label">Position</th>
            <th scope="col" className="label">Menge</th>
            <th scope="col" className={`label ${styles.moneyHead}`}>Fläche</th>
            <th scope="col" className={`label ${styles.moneyHead}`}>EK/Stk</th>
            {/* „Zeile", not „gesamt" (S2-6 F-3): the one EK sum is „EK Roste
                (ohne Konsolen)" in the totals (D-9), and a head saying
                „gesamt" over a line read like the sum D-9 rules out. */}
            <th scope="col" className={`label ${styles.moneyHead}`}>EK Zeile</th>
            <th scope="col" className={`label ${styles.moneyHead}`}>VK/Stk</th>
            <th scope="col" className={`label ${styles.moneyHead}`}>VK Zeile</th>
          </tr>
        </thead>
        {groups.map((group) => (
          <tbody key={group.kind}>
            <tr className={styles.group}>
              <th scope="rowgroup" colSpan={7}>
                {group.title}
                {group.note ? <span className={styles.groupNote}>{group.note}</span> : null}
              </th>
            </tr>
            {group.lines.map((line) => {
              const sentence = line.sourceText;
              // A priced Pauschale's note is part of its sentence; one
              // without a price has no sentence, so its note („Über 2000 kg
              // — Fracht auf Anfrage.") stays on the line instead. A
              // bracket's note says why its article is that one (a
              // substituted family, a retired article — `consoles.py`), the
              // half of its provenance the article number alone does not.
              const showNote = line.note !== "" && (line.priceRule !== "pauschale" || sentence === null);
              // Tri-state (N-16): fully blocked only when there is no price
              // at all. A soft-gapped line keeps its four amounts and gets a
              // second line under the label instead of losing its cells.
              const blockedMessage =
                line.blockedReason !== null && line.vkTotal === null ? line.blockedReason.message : null;
              const gapMessage =
                line.blockedReason !== null && line.vkTotal !== null ? line.blockedReason.message : null;
              return (
                <Fragment key={line.lineNo}>
                  <tr
                    className={blockedMessage !== null ? styles.blocked : undefined}
                    data-blocked={blockedMessage !== null ? "true" : "false"}
                  >
                    <th scope="row" className={styles.position} title={sentence ?? undefined}>
                      <span className={`num ${styles.lineNo}`}>{line.lineNo}</span>
                      {line.label}
                      {showNote ? <span className={styles.lineNote}>{line.note}</span> : null}
                      {gapMessage !== null ? (
                        <span className={styles.gapNote} data-gap="true">
                          Lücke — {gapMessage}
                        </span>
                      ) : null}
                    </th>
                    <td className="num">
                      {line.qty} {line.unit}
                    </td>
                    <td className={`num ${styles.money}`}>{formatArea(line.areaM2)}</td>
                    {blockedMessage !== null ? (
                      <td colSpan={4} className={styles.blockedCell}>
                        Preis fehlt — {blockedMessage}
                      </td>
                    ) : (
                      <>
                        <td className={`num ${styles.money}`}>{formatMoney(line.ekUnit)}</td>
                        <td className={`num ${styles.money}`}>{formatMoney(line.ekTotal)}</td>
                        <td className={`num ${styles.money}`}>{formatMoney(line.vkUnit)}</td>
                        <td className={`num ${styles.money}`}>{formatMoney(line.vkTotal)}</td>
                      </>
                    )}
                  </tr>
                  {sentence !== null ? (
                    <tr className={styles.source}>
                      <td colSpan={7}>
                        <PriceSourceNote sentence={sentence} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}
